import {
	type AttachmentJSONObject,
	type IkeActionFileGetData,
	type IkeActionFileInfo,
	type IkeActionFileInfoResponse,
	type IkeActionFileParse,
	type IkeActionFileParseResponse,
	type IkeActionVirusScan,
} from '@ikegps/core-api';
import { IkeRequestHeader } from '@ikegps/core-api';
import type { IkeRestDepartment } from './rest.department';

export class IkeRestDepartmentAttachment {
	boundDepartment: IkeRestDepartment;

	constructor(boundDepartment: IkeRestDepartment) {
		this.boundDepartment = boundDepartment;
	}

	async set(
		fileName: string,
		buffer: Uint8Array<ArrayBuffer> | Buffer<ArrayBufferLike> | string,
		contentType: string
	): Promise<AttachmentJSONObject> {
		const core = this.boundDepartment.boundUser.core;
		const headers = core.headers(this.boundDepartment.boundUser.accessToken, this.boundDepartment.departmentId);

		headers[IkeRequestHeader.FILE_NAME] = fileName;
		headers[IkeRequestHeader.CONTENT_TYPE] = contentType;

		const body = typeof buffer === 'string' ? new TextEncoder().encode(buffer) : new Uint8Array(buffer);
		const response = await core.fetch(core.path(`/v1/action/attachment.create`), { method: 'POST', body, headers });

		if (response.ok) {
			await core.validateIsJson(response);
			return response.json();
		}
		return core.handleError(response);
	}

	async get(attachmentId: string) {
		const core = this.boundDepartment.boundUser.core;

		const response = await core.actionRaw<IkeActionFileGetData>(
			'attachment.data',
			{ attachmentId },
			this.boundDepartment.boundUser.accessToken,
			this.boundDepartment.departmentId
		);

		if (response.ok) {
			return response.body;
		}
		return core.handleError(response);
	}

	async info(attachmentId: string): Promise<IkeActionFileInfoResponse> {
		return this.boundDepartment.action<IkeActionFileInfo>('attachment.get', { attachmentId });
	}

	async parse(attachmentId: string): Promise<IkeActionFileParseResponse> {
		const core = this.boundDepartment.boundUser.core;

		const response = await core.actionRaw<IkeActionFileParse>(
			'attachment.parse',
			{ attachmentId },
			this.boundDepartment.boundUser.accessToken,
			this.boundDepartment.departmentId
		);

		if (response.ok) {
			await core.validateIsJson(response);
			return response.json();
		}
		return core.handleError(response);
	}

	async virusScan(attachmentId: string) {
		const core = this.boundDepartment.boundUser.core;

		const response = await core.actionRaw<IkeActionVirusScan>(
			'attachment.virus.scan',
			{ attachmentId },
			this.boundDepartment.boundUser.accessToken,
			this.boundDepartment.departmentId
		);

		if (response.ok) {
			await core.validateIsJson(response);
			return response.json();
		}
		return core.handleError(response);
	}
}
